/**
 * Every language in the world by ISO 639-1 (184 codes). The data behind every language
 * picker: the host view of Settings -> Languages (superadmin), where any world language
 * can be added to the platform catalogue without typing its code by hand, and the shared
 * `LocaleCombobox`.
 *
 * `native`  = the name in the language itself (its endonym, e.g. "Polski") - this is what
 *             gets stored as the catalogue label (matching the existing et/ru/... style).
 * `english` = the English name - only a search aid (the Combobox hint), so that a
 *             superadmin can find a language by its Latin name too.
 *
 * The source of truth stays the server catalogue (Setting i18n.platformLocales); this
 * list is purely the data behind a UI picker. Code format: two lower-case letters (the
 * regex check lives in the API's lib/platform-locales.ts, isLocaleCode).
 */
export interface WorldLanguage {
  /** ISO 639-1 code (two lower-case letters). */
  code: string;
  /** The name in the language itself (its autonym). */
  native: string;
  /** The English name (a search aid). */
  english: string;
}

export const WORLD_LANGUAGES: WorldLanguage[] = [
  { code: "aa", native: "Afaraf", english: "Afar" },
  { code: "ab", native: "аҧсуа бызшәа", english: "Abkhazian" },
  { code: "ae", native: "avesta", english: "Avestan" },
  { code: "af", native: "Afrikaans", english: "Afrikaans" },
  { code: "ak", native: "Akan", english: "Akan" },
  { code: "am", native: "አማርኛ", english: "Amharic" },
  { code: "an", native: "aragonés", english: "Aragonese" },
  { code: "ar", native: "العربية", english: "Arabic" },
  { code: "as", native: "অসমীয়া", english: "Assamese" },
  { code: "av", native: "авар мацӀ", english: "Avaric" },
  { code: "ay", native: "aymar aru", english: "Aymara" },
  { code: "az", native: "azərbaycan dili", english: "Azerbaijani" },
  { code: "ba", native: "башҡорт теле", english: "Bashkir" },
  { code: "be", native: "беларуская мова", english: "Belarusian" },
  { code: "bg", native: "български език", english: "Bulgarian" },
  { code: "bh", native: "भोजपुरी", english: "Bihari" },
  { code: "bi", native: "Bislama", english: "Bislama" },
  { code: "bm", native: "bamanankan", english: "Bambara" },
  { code: "bn", native: "বাংলা", english: "Bengali" },
  { code: "bo", native: "བོད་ཡིག", english: "Tibetan" },
  { code: "br", native: "brezhoneg", english: "Breton" },
  { code: "bs", native: "bosanski jezik", english: "Bosnian" },
  { code: "ca", native: "català", english: "Catalan" },
  { code: "ce", native: "нохчийн мотт", english: "Chechen" },
  { code: "ch", native: "Chamoru", english: "Chamorro" },
  { code: "co", native: "corsu", english: "Corsican" },
  { code: "cr", native: "ᓀᐦᐃᔭᐍᐏᐣ", english: "Cree" },
  { code: "cs", native: "čeština", english: "Czech" },
  { code: "cu", native: "ѩзыкъ словѣньскъ", english: "Church Slavic" },
  { code: "cv", native: "чӑваш чӗлхи", english: "Chuvash" },
  { code: "cy", native: "Cymraeg", english: "Welsh" },
  { code: "da", native: "dansk", english: "Danish" },
  { code: "de", native: "Deutsch", english: "German" },
  { code: "dv", native: "ދިވެހި", english: "Divehi" },
  { code: "dz", native: "རྫོང་ཁ", english: "Dzongkha" },
  { code: "ee", native: "Eʋegbe", english: "Ewe" },
  { code: "el", native: "Ελληνικά", english: "Greek" },
  { code: "en", native: "English", english: "English" },
  { code: "eo", native: "Esperanto", english: "Esperanto" },
  { code: "es", native: "Español", english: "Spanish" },
  { code: "et", native: "Eesti", english: "Estonian" },
  { code: "eu", native: "euskara", english: "Basque" },
  { code: "fa", native: "فارسی", english: "Persian" },
  { code: "ff", native: "Fulfulde", english: "Fulah" },
  { code: "fi", native: "Suomi", english: "Finnish" },
  { code: "fj", native: "vosa Vakaviti", english: "Fijian" },
  { code: "fo", native: "føroyskt", english: "Faroese" },
  { code: "fr", native: "Français", english: "French" },
  { code: "fy", native: "Frysk", english: "Western Frisian" },
  { code: "ga", native: "Gaeilge", english: "Irish" },
  { code: "gd", native: "Gàidhlig", english: "Scottish Gaelic" },
  { code: "gl", native: "Galego", english: "Galician" },
  { code: "gn", native: "Avañe'ẽ", english: "Guarani" },
  { code: "gu", native: "ગુજરાતી", english: "Gujarati" },
  { code: "gv", native: "Gaelg", english: "Manx" },
  { code: "ha", native: "هَوُسَ", english: "Hausa" },
  { code: "he", native: "עברית", english: "Hebrew" },
  { code: "hi", native: "हिन्दी", english: "Hindi" },
  { code: "ho", native: "Hiri Motu", english: "Hiri Motu" },
  { code: "hr", native: "hrvatski jezik", english: "Croatian" },
  { code: "ht", native: "Kreyòl ayisyen", english: "Haitian Creole" },
  { code: "hu", native: "magyar", english: "Hungarian" },
  { code: "hy", native: "Հայերեն", english: "Armenian" },
  { code: "hz", native: "Otjiherero", english: "Herero" },
  { code: "ia", native: "Interlingua", english: "Interlingua" },
  { code: "id", native: "Bahasa Indonesia", english: "Indonesian" },
  { code: "ie", native: "Interlingue", english: "Interlingue" },
  { code: "ig", native: "Asụsụ Igbo", english: "Igbo" },
  { code: "ii", native: "ꆈꌠ꒿ Nuosuhxop", english: "Sichuan Yi" },
  { code: "ik", native: "Iñupiaq", english: "Inupiaq" },
  { code: "io", native: "Ido", english: "Ido" },
  { code: "is", native: "Íslenska", english: "Icelandic" },
  { code: "it", native: "Italiano", english: "Italian" },
  { code: "iu", native: "ᐃᓄᒃᑎᑐᑦ", english: "Inuktitut" },
  { code: "ja", native: "日本語", english: "Japanese" },
  { code: "jv", native: "basa Jawa", english: "Javanese" },
  { code: "ka", native: "ქართული", english: "Georgian" },
  { code: "kg", native: "Kikongo", english: "Kongo" },
  { code: "ki", native: "Gĩkũyũ", english: "Kikuyu" },
  { code: "kj", native: "Kuanyama", english: "Kuanyama" },
  { code: "kk", native: "қазақ тілі", english: "Kazakh" },
  { code: "kl", native: "kalaallisut", english: "Kalaallisut" },
  { code: "km", native: "ខ្មែរ", english: "Khmer" },
  { code: "kn", native: "ಕನ್ನಡ", english: "Kannada" },
  { code: "ko", native: "한국어", english: "Korean" },
  { code: "kr", native: "Kanuri", english: "Kanuri" },
  { code: "ks", native: "कश्मीरी", english: "Kashmiri" },
  { code: "ku", native: "Kurdî", english: "Kurdish" },
  { code: "kv", native: "коми кыв", english: "Komi" },
  { code: "kw", native: "Kernewek", english: "Cornish" },
  { code: "ky", native: "Кыргызча", english: "Kyrgyz" },
  { code: "la", native: "latine", english: "Latin" },
  { code: "lb", native: "Lëtzebuergesch", english: "Luxembourgish" },
  { code: "lg", native: "Luganda", english: "Ganda" },
  { code: "li", native: "Limburgs", english: "Limburgish" },
  { code: "ln", native: "Lingála", english: "Lingala" },
  { code: "lo", native: "ພາສາລາວ", english: "Lao" },
  { code: "lt", native: "Lietuvių", english: "Lithuanian" },
  { code: "lu", native: "Tshiluba", english: "Luba-Katanga" },
  { code: "lv", native: "Latviešu", english: "Latvian" },
  { code: "mg", native: "fiteny malagasy", english: "Malagasy" },
  { code: "mh", native: "Kajin M̧ajeḷ", english: "Marshallese" },
  { code: "mi", native: "te reo Māori", english: "Maori" },
  { code: "mk", native: "македонски јазик", english: "Macedonian" },
  { code: "ml", native: "മലയാളം", english: "Malayalam" },
  { code: "mn", native: "Монгол хэл", english: "Mongolian" },
  { code: "mr", native: "मराठी", english: "Marathi" },
  { code: "ms", native: "Bahasa Melayu", english: "Malay" },
  { code: "mt", native: "Malti", english: "Maltese" },
  { code: "my", native: "ဗမာစာ", english: "Burmese" },
  { code: "na", native: "Ekakairũ Naoero", english: "Nauru" },
  { code: "nb", native: "Norsk Bokmål", english: "Norwegian Bokmal" },
  { code: "nd", native: "isiNdebele", english: "North Ndebele" },
  { code: "ne", native: "नेपाली", english: "Nepali" },
  { code: "ng", native: "Owambo", english: "Ndonga" },
  { code: "nl", native: "Nederlands", english: "Dutch" },
  { code: "nn", native: "Norsk Nynorsk", english: "Norwegian Nynorsk" },
  { code: "no", native: "Norsk", english: "Norwegian" },
  { code: "nr", native: "isiNdebele", english: "South Ndebele" },
  { code: "nv", native: "Diné bizaad", english: "Navajo" },
  { code: "ny", native: "chiCheŵa", english: "Chichewa" },
  { code: "oc", native: "occitan", english: "Occitan" },
  { code: "oj", native: "ᐊᓂᔑᓈᐯᒧᐎᓐ", english: "Ojibwe" },
  { code: "om", native: "Afaan Oromoo", english: "Oromo" },
  { code: "or", native: "ଓଡ଼ିଆ", english: "Oriya" },
  { code: "os", native: "ирон æвзаг", english: "Ossetian" },
  { code: "pa", native: "ਪੰਜਾਬੀ", english: "Punjabi" },
  { code: "pi", native: "पालि", english: "Pali" },
  { code: "pl", native: "Polski", english: "Polish" },
  { code: "ps", native: "پښتو", english: "Pashto" },
  { code: "pt", native: "Português", english: "Portuguese" },
  { code: "qu", native: "Runa Simi", english: "Quechua" },
  { code: "rm", native: "rumantsch grischun", english: "Romansh" },
  { code: "rn", native: "Ikirundi", english: "Rundi" },
  { code: "ro", native: "Română", english: "Romanian" },
  { code: "ru", native: "Русский", english: "Russian" },
  { code: "rw", native: "Ikinyarwanda", english: "Kinyarwanda" },
  { code: "sa", native: "संस्कृतम्", english: "Sanskrit" },
  { code: "sc", native: "sardu", english: "Sardinian" },
  { code: "sd", native: "सिन्धी", english: "Sindhi" },
  { code: "se", native: "Davvisámegiella", english: "Northern Sami" },
  { code: "sg", native: "yângâ tî sängö", english: "Sango" },
  { code: "si", native: "සිංහල", english: "Sinhala" },
  { code: "sk", native: "slovenčina", english: "Slovak" },
  { code: "sl", native: "slovenščina", english: "Slovenian" },
  { code: "sm", native: "gagana fa'a Samoa", english: "Samoan" },
  { code: "sn", native: "chiShona", english: "Shona" },
  { code: "so", native: "Soomaaliga", english: "Somali" },
  { code: "sq", native: "Shqip", english: "Albanian" },
  { code: "sr", native: "српски језик", english: "Serbian" },
  { code: "ss", native: "SiSwati", english: "Swati" },
  { code: "st", native: "Sesotho", english: "Southern Sotho" },
  { code: "su", native: "Basa Sunda", english: "Sundanese" },
  { code: "sv", native: "Svenska", english: "Swedish" },
  { code: "sw", native: "Kiswahili", english: "Swahili" },
  { code: "ta", native: "தமிழ்", english: "Tamil" },
  { code: "te", native: "తెలుగు", english: "Telugu" },
  { code: "tg", native: "тоҷикӣ", english: "Tajik" },
  { code: "th", native: "ไทย", english: "Thai" },
  { code: "ti", native: "ትግርኛ", english: "Tigrinya" },
  { code: "tk", native: "Türkmen", english: "Turkmen" },
  { code: "tl", native: "Wikang Tagalog", english: "Tagalog" },
  { code: "tn", native: "Setswana", english: "Tswana" },
  { code: "to", native: "faka Tonga", english: "Tonga" },
  { code: "tr", native: "Türkçe", english: "Turkish" },
  { code: "ts", native: "Xitsonga", english: "Tsonga" },
  { code: "tt", native: "татар теле", english: "Tatar" },
  { code: "tw", native: "Twi", english: "Twi" },
  { code: "ty", native: "Reo Tahiti", english: "Tahitian" },
  { code: "ug", native: "ئۇيغۇرچە", english: "Uyghur" },
  { code: "uk", native: "Українська", english: "Ukrainian" },
  { code: "ur", native: "اردو", english: "Urdu" },
  { code: "uz", native: "Oʻzbek", english: "Uzbek" },
  { code: "ve", native: "Tshivenḓa", english: "Venda" },
  { code: "vi", native: "Tiếng Việt", english: "Vietnamese" },
  { code: "vo", native: "Volapük", english: "Volapuk" },
  { code: "wa", native: "walon", english: "Walloon" },
  { code: "wo", native: "Wollof", english: "Wolof" },
  { code: "xh", native: "isiXhosa", english: "Xhosa" },
  { code: "yi", native: "ייִדיש", english: "Yiddish" },
  { code: "yo", native: "Yorùbá", english: "Yoruba" },
  { code: "za", native: "Saɯ cueŋƅ", english: "Zhuang" },
  { code: "zh", native: "中文", english: "Chinese" },
  { code: "zu", native: "isiZulu", english: "Zulu" },
];

/** Fast lookup by code (e.g. to find the autonym when a language is added to the catalogue). */
export const WORLD_LANGUAGE_BY_CODE: Record<string, WorldLanguage> = Object.fromEntries(
  WORLD_LANGUAGES.map((l) => [l.code, l]),
);
